import styled, { x } from '@xstyled/styled-components';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { IMAGE_MAP, AUDIO_POPUP, POPUP } from '..';
import { Item, ItemDefinition, registry, RenderProps } from '../Item';
import {
  SequenceStore,
  useSequenceStore
} from '~/training/sequence/SequenceStore';
import { select } from '~/lib/select';
import { escapeHtml, stripHtml } from '~/lib/sanitization';
import {
  useEditorStore,
  EditorChapter,
  EditorMediaFile,
  SequenceItem
} from '~/shell/Store';
import { EditBg } from '~/editor/buttons/EditBg';
import AddTextAudioModal from '~/editor/addTextAudioModal/AddTextAudioModal';
import AudioPoint from '~/editor/audioPoint/AudioPoint';
import { parseAudioPointId } from '../image-map2/audioPointIdUtils';
import {
  nudgeDraggedHotspotAwayFromFixed,
  usableHotspotImageSize
} from '~/training/sequence/templates/translators/interactive4Overlap';
import { ImageMapPopupLayoutContext } from './ImageMapPopupLayoutContext';

interface AudioPointData {
  id: string;
  text: string;
  subtitle?: string;
  body?: string;
  iconType?: string | null;
  iconValue?: number | null;
  audio: { id: number; mediaUrl: string; originalFilename?: string } | null;
  coordinates: { x: number; y: number };
}

interface TempAudioPoint {
  id: number | string;
  type: string;
  left: number;
  top: number;
  values: {
    title?: string;
    body?: string;
    mediaUrl?: string;
    name?: string;
    iconType?: string | null;
    iconValue?: number | null;
  };
}

interface Props {
  imageUrl: string;
  body?: string;
  w?: string;
  h?: string;
  /** Template-specific (e.g. Interactive4): tune anchored popups; see ImageMapPopupLayoutContext. */
  popupFixedOffsetLeftPx?: number;
  popupFixedOffsetTopPx?: number;
  clampPopupToImageMap?: boolean;
  /** Template-specific (Interactive4): its hotspots without audio show learners a subtitle. */
  offerPopupSubtitle?: boolean;
}

export type ImageMapDefinition = ItemDefinition<typeof IMAGE_MAP, Props>;

const Background = styled.div<{ imageUrl: string; w: string; h: string }>`
  width: ${p => p.w};
  max-width: 100%;
  box-sizing: border-box;
  height: ${p => p.h};
  background-size: cover;
  background-position: center;
  background-image: url(${p => p.imageUrl});
  filter: brightness(1) !important;
  position: relative;
`;

/** CAT-1438 / EL–E289: page needs a heading; keep the same chip look as before. */
const Instructions = styled.h1`
  position: fixed;
  left: 50%;
  top: 60px;
  transform: translate(-50%, -50%);
  margin: 0 auto;
  background-color: white;
  padding: 10px 25px;
  border-radius: 20px;
  opacity: 0.8;
  font-size: inherit;
  font-weight: 400;
  line-height: inherit;
`;

const sequenceSelector = (s: SequenceStore) => select(s, 'blockers');

export class ImageMap extends Item<typeof IMAGE_MAP, Props> {
  protected render({
    imageUrl,
    body,
    children,
    w = '100%',
    h = '100vh',
    popupFixedOffsetLeftPx,
    popupFixedOffsetTopPx,
    clampPopupToImageMap,
    offerPopupSubtitle = false,
    ...props
  }: RenderProps<Props>) {
    const { blockers } = useSequenceStore(sequenceSelector);
    const {
      contentEditable,
      addSubItem,
      sequence,
      apiService,
      setSequence,
      updateSequence,
      setForceUpdateSequence,
      training,
      updateFile,
      setTraining
    } = useEditorStore();
    const divElRef = useRef<HTMLDivElement>(null);
    const backgroundRef = useRef<HTMLDivElement>(null);
    const [showAddTextAudioModal, setShowAddTextAudioModal] = useState(false);
    const [clickPosition, setClickPosition] = useState<{
      x: number;
      y: number;
    } | null>(null);
    const [editingAudioPoint, setEditingAudioPoint] =
      useState<AudioPointData | null>(null);
    const [movedAudioPoints, setMovedAudioPoints] = useState<
      Record<string, { x: number; y: number }>
    >({});
    const [tempAudioPoints, setTempAudioPoints] = useState<TempAudioPoint[]>(
      []
    );
    const [isCreatingAudioPoint, setIsCreatingAudioPoint] = useState(false);
    const [creatingPosition, setCreatingPosition] = useState<{
      x: number;
      y: number;
    } | null>(null);
    const [isEditingAudioPoint, setIsEditingAudioPoint] = useState(false);
    const [editingPosition, setEditingPosition] = useState<{
      x: number;
      y: number;
    } | null>(null);
    const [isMovingAudioPoint, setIsMovingAudioPoint] = useState(false);
    const [movingPosition, setMovingPosition] = useState<{
      x: number;
      y: number;
    } | null>(null);
    const [mousePosition, setMousePosition] = useState<{
      x: number;
      y: number;
    } | null>(null);

    const cachedImageDimensionsRef = useRef<{
      width: number;
      height: number;
    } | null>(null);

    useEffect(() => {
      if (!sequence?.items) return;
      const item =
        sequence.items.find((i: SequenceItem) => i.id === props.id) ||
        sequence.items.find(
          (i: SequenceItem) =>
            i.__typename === 'ImageMap' || i.type === 'ImageMap'
        ) ||
        sequence.items.find(
          (i: SequenceItem) =>
            i.__typename === 'Background' || i.type === 'Background'
        );
      const rawWidth = item?.pictures?.[0]?.width;
      const rawHeight = item?.pictures?.[0]?.height;
      const cachedSize = usableHotspotImageSize(
        rawWidth as number,
        rawHeight as number
      );
      if (cachedSize) {
        cachedImageDimensionsRef.current = cachedSize;
      } else if (rawWidth != null || rawHeight != null) {
        // Picture present but unusable (1×1 solid): drop the stale real-image
        // cache so edit/move fall back to the same 1024 basis as the translator.
        cachedImageDimensionsRef.current = null;
      }
    }, [sequence?.items, props.id]);

    const handleBackgroundClick = (event: React.MouseEvent<HTMLDivElement>) => {
      // Only handle clicks in editor mode
      if (!contentEditable) return;

      // Prevent default behavior
      event.preventDefault();
      event.stopPropagation();

      // Get the background element and its bounding box
      const backgroundElement = event.currentTarget;
      const rect = backgroundElement.getBoundingClientRect();

      // Calculate click position relative to the background as percentages
      const x = ((event.clientX - rect.left) / rect.width) * 100;
      const y = ((event.clientY - rect.top) / rect.height) * 100;

      // Store the click position and show the add text and audio modal
      setClickPosition({
        x: Math.round(x * 100) / 100,
        y: Math.round(y * 100) / 100
      });

      setShowAddTextAudioModal(true);
    };

    const handleBackgroundMouseMove = (
      event: React.MouseEvent<HTMLDivElement>
    ) => {
      // Only track mouse in editor mode
      if (!contentEditable) {
        setMousePosition(null);
        return;
      }

      // Update mouse position for tooltip
      setMousePosition({
        x: event.clientX,
        y: event.clientY
      });
    };

    const handleBackgroundMouseLeave = () => {
      // Hide tooltip when mouse leaves background
      setMousePosition(null);
    };

    const updateBackgroundImage = ({
      id,
      file,
      type
    }: {
      id: string;
      file: unknown;
      type: string;
    }) => {
      const imageMapItem = sequence?.items?.find(
        (item: SequenceItem) => item.id === props.id
      );
      if (!imageMapItem) return;

      const prevSize = usableHotspotImageSize(
        Number(imageMapItem?.pictures?.[0]?.width),
        Number(imageMapItem?.pictures?.[0]?.height)
      );
      const prevWidth = prevSize?.width ?? 1024;
      const prevHeight = prevSize?.height ?? 1024;
      const subitems = imageMapItem?.items || [];
      const newSize = usableHotspotImageSize(
        (file as { width?: number; height?: number })?.width,
        (file as { width?: number; height?: number })?.height
      );
      const newWidth = newSize?.width ?? 1024;
      const newHeight = newSize?.height ?? 1024;

      const updates: Array<{ id: string; coords: string }> = [];

      subitems.forEach((subitem: SequenceItem, index: number) => {
        const subitemId = String(subitem.id ?? `${props.id}-${index}`);
        const _id = subitemId.split('-')[0];

        const movedPosition =
          movedAudioPoints[subitemId] || movedAudioPoints[_id];

        let percentX: number;
        let percentY: number;

        if (movedPosition?.x != null && movedPosition?.y != null) {
          percentX = movedPosition.x;
          percentY = movedPosition.y;
        } else {
          const parts = (subitem.coordinates || '').split(',');
          const oldPxX = parseFloat(parts[0]) || 0;
          const oldPxY = parseFloat(parts[1]) || 0;
          percentX = prevWidth > 0 ? (oldPxX * 100) / prevWidth : 0;
          percentY = prevHeight > 0 ? (oldPxY * 100) / prevHeight : 0;
        }

        const newPxX = (percentX / 100) * newWidth;
        const newPxY = (percentY / 100) * newHeight;

        updates.push({ id: _id, coords: `${newPxX},${newPxY}` });
      });

      updates.forEach(u => {
        updateSequence(u.id, 'coordinates', u.coords);
        updateSequence(u.id, 'settings', { coordinates: u.coords });
      });

      updateFile(id, file as EditorMediaFile, type);

      if (prevSize) {
        cachedImageDimensionsRef.current = prevSize;
      }
    };

    // A hotspot's text counts as saved only once the server has it: until
    // then the panel stays open, and a refused save keeps its draft (false).
    const handleTextAudioSubmit = async (data: {
      text: string;
      subtitle?: string;
      body?: string;
      audio: { id: number; mediaUrl: string; originalFilename?: string } | null;
      coordinates: { x: number; y: number };
      iconType: 'icon' | 'number';
      iconValue: number | null;
    }): Promise<boolean> => {
      try {
        if (!sequence?.id) {
          console.error('Sequence not available');
          return false;
        }
        // Check if we're editing an existing audio point
        if (editingAudioPoint) {
          // Parse the audio point ID using the utility function
          const idResult = parseAudioPointId(editingAudioPoint.id);

          // Interactive4 builds ids as `${backendId}-${index}`, but
          // parseAudioPointId returns the LAST segment (the index, e.g. 0/1/2),
          // so the backend id ends up wrong (it's why edits posted to /subitems/0).
          // The real backend id is always the FIRST segment — use the same
          // workaround as in handleMoveAudioPoint to recover it.
          const _editIds = String(editingAudioPoint.id).split('-');
          const parsedFirstSegment = parseInt(_editIds[0], 10);
          if (!isNaN(parsedFirstSegment)) {
            idResult.numericId = parsedFirstSegment;
          }

          // Only handle real backend IDs
          if (
            idResult.isTemporary ||
            !idResult.isValid ||
            idResult.numericId === null
          ) {
            console.error(
              'Invalid audio point ID for editing:',
              idResult.originalId
            );
            setShowAddTextAudioModal(false);
            setEditingAudioPoint(null);
            setClickPosition(null);
            return true;
          }

          const numericId = idResult.numericId;
          if (!apiService) return false;

          // The text the author changed, with the marker, in one request.
          const saved = await apiService.updateSubItemBatch(numericId, {
            ...(data.text !== editingAudioPoint.text
              ? { title: data.text }
              : {}),
            // A subtitle the panel did not show is left as the hotspot has
            // it, and one only re-spaced is not sent.
            ...(data.subtitle !== undefined &&
            data.subtitle !== (editingAudioPoint.subtitle ?? '').trim()
              ? { subtitle: escapeHtml(data.subtitle) }
              : {}),
            ...((data.body || '') !== (editingAudioPoint.body || '')
              ? { body: data.body || '' }
              : {}),
            icon_type: data.iconType,
            ...(data.iconType === 'number' && data.iconValue != null
              ? { icon_value: data.iconValue }
              : {})
          });
          if (saved?.error) return false;

          // The text is saved and the panel closes; the media and position
          // follow behind a loader at the hotspot, then a fresh copy.
          setIsEditingAudioPoint(true);
          setEditingPosition({
            x: editingAudioPoint.coordinates.x || 0,
            y: editingAudioPoint.coordinates.y || 0
          });

          (async () => {
            try {
              // Handle audio update - can be empty, new, or existing
              if (data.audio?.id && data.audio.id !== 0) {
                // Use updateFile to add/update audio (same as ImageMap2)
                const { updateFile } = useEditorStore.getState();
                updateFile(
                  numericId.toString(),
                  { id: data.audio.id, mediaUrl: data.audio.mediaUrl },
                  'audios'
                );

                // Update coordinates
                const currentImageMapItem = sequence.items?.find(
                  (item: SequenceItem) => item.id === props.id
                );
                const pictureSize = usableHotspotImageSize(
                  currentImageMapItem?.pictures?.[0]?.width as number,
                  currentImageMapItem?.pictures?.[0]?.height as number
                );
                const IMAGE_WIDTH =
                  pictureSize?.width ||
                  cachedImageDimensionsRef.current?.width ||
                  1024;
                const IMAGE_HEIGHT =
                  pictureSize?.height ||
                  cachedImageDimensionsRef.current?.height ||
                  1024;
                if (pictureSize) {
                  cachedImageDimensionsRef.current = pictureSize;
                }
                const absoluteX = (data.coordinates.x * IMAGE_WIDTH) / 100;
                const absoluteY = (data.coordinates.y * IMAGE_HEIGHT) / 100;
                const coordinatesString = `${Math.round(
                  absoluteX
                )},${Math.round(absoluteY)}`;

                await apiService.updateSubItem(
                  numericId,
                  'coordinates',
                  coordinatesString
                );
              } else {
                // No audio provided - check if we need to remove existing audio or just update text
                const currentItem = sequence.items
                  ?.flatMap((item: SequenceItem) => item.items || [])
                  .find((item: SequenceItem) => item.id == numericId);

                const hadAudioBefore = (currentItem?.audios?.length ?? 0) > 0;

                const currentImageMapItem = sequence.items?.find(
                  (item: SequenceItem) => item.id === props.id
                );
                const pictureSize = usableHotspotImageSize(
                  currentImageMapItem?.pictures?.[0]?.width as number,
                  currentImageMapItem?.pictures?.[0]?.height as number
                );
                const IMAGE_WIDTH =
                  pictureSize?.width ||
                  cachedImageDimensionsRef.current?.width ||
                  1024;
                const IMAGE_HEIGHT =
                  pictureSize?.height ||
                  cachedImageDimensionsRef.current?.height ||
                  1024;
                if (pictureSize) {
                  cachedImageDimensionsRef.current = pictureSize;
                }
                const absoluteX = (data.coordinates.x * IMAGE_WIDTH) / 100;
                const absoluteY = (data.coordinates.y * IMAGE_HEIGHT) / 100;
                const coordinatesString = `${Math.round(
                  absoluteX
                )},${Math.round(absoluteY)}`;

                await apiService.updateSubItem(
                  numericId,
                  'coordinates',
                  coordinatesString
                );

                if (hadAudioBefore) {
                  await apiService.updateSubItem(numericId, 'audio_ids', []);
                }
              }

              // Force refresh from backend
              const freshSequenceData = await apiService.getSequenceInfo(
                sequence.id
              );

              if (freshSequenceData?.sequence) {
                // Update sequence with fresh data
                setSequence(freshSequenceData.sequence);

                // Also update training to keep it in sync (following Header.tsx pattern)
                if (training) {
                  const updatedTraining = {
                    ...training,
                    chapters: training.chapters.map(
                      (chapter: EditorChapter) => ({
                        ...chapter,
                        chapterSequences: chapter?.chapterSequences?.map(
                          (
                            chapterSequence: EditorChapter['chapterSequences'][number]
                          ) => {
                            return chapterSequence.sequence.id === sequence.id
                              ? {
                                  ...chapterSequence,
                                  sequence: {
                                    ...chapterSequence.sequence,
                                    ...freshSequenceData.sequence
                                  }
                                }
                              : chapterSequence;
                          }
                        )
                      })
                    )
                  };
                  setTraining(updatedTraining);
                }

                // CRITICAL: Set force update flag to trigger re-render
                setForceUpdateSequence(true);

                // Hide editing loader after successful update
                setIsEditingAudioPoint(false);
                setEditingPosition(null);
              } else {
                // Hide loader if no fresh data received
                setIsEditingAudioPoint(false);
                setEditingPosition(null);
              }
            } catch (error) {
              // On error, still try to refresh to restore correct state
              try {
                const freshSequenceData = await apiService.getSequenceInfo(
                  sequence.id
                );
                if (freshSequenceData?.sequence) {
                  setSequence(freshSequenceData.sequence);

                  // Also update training on error recovery
                  if (training) {
                    const updatedTraining = {
                      ...training,
                      chapters: training.chapters.map(
                        (chapter: EditorChapter) => ({
                          ...chapter,
                          chapterSequences: chapter?.chapterSequences?.map(
                            (
                              chapterSequence: EditorChapter['chapterSequences'][number]
                            ) => {
                              return chapterSequence.sequence.id === sequence.id
                                ? {
                                    ...chapterSequence,
                                    sequence: {
                                      ...chapterSequence.sequence,
                                      ...freshSequenceData.sequence
                                    }
                                  }
                                : chapterSequence;
                            }
                          )
                        })
                      )
                    };
                    setTraining(updatedTraining);
                  }

                  // Force update on error recovery too
                  setForceUpdateSequence(true);

                  // Hide editing loader after error recovery
                  setIsEditingAudioPoint(false);
                  setEditingPosition(null);
                }
              } catch (refreshError) {
                // Hide loader even if refresh fails
                setIsEditingAudioPoint(false);
                setEditingPosition(null);
              }
            }
          })();

          return true;
        }

        if (!addSubItem) {
          console.error('addSubItem not available');
          return false;
        }

        // Determine popup type based on whether audio is provided
        // const popupType = data.audio ? AUDIO_POPUP : POPUP;

        // Create new popup item structure
        const newPopupItem = data.audio
          ? {
              type: AUDIO_POPUP,
              items: [],
              values: {
                left: data.coordinates.x,
                top: data.coordinates.y,
                name: data.audio?.originalFilename || 'Audio Point',
                title: data.text,
                body: data.body || '',
                mediaUrl: data.audio?.mediaUrl || '',
                subtitlesUrl: '', // Could be added later if needed
                blocking: false,
                replay: true,
                isPopupCentered: false,
                scroll: true,
                pointNumber:
                  React.Children.toArray(children).filter(
                    (child: React.ReactElement) => {
                      if (
                        child?.props?.type === AUDIO_POPUP ||
                        child?.props?.type === POPUP
                      ) {
                        const left = child.props.left;
                        const top = child.props.top;
                        return (
                          !isNaN(left) &&
                          !isNaN(top) &&
                          !(left === 0 && top === 0) &&
                          left != null &&
                          top != null
                        );
                      }
                      return false;
                    }
                  ).length + 1 // Number for the point
              }
            }
          : {
              type: POPUP,
              values: {
                btnTitle: '+',
                left: data.coordinates.x,
                top: data.coordinates.y,
                title: data.text,
                subtitle: data.subtitle ? escapeHtml(data.subtitle) : '',
                body: data.body || '',
                blocking: true
              }
            };

        // Create the sub-item data for the API
        // Convert percentage coordinates to absolute pixels for backend
        // Get actual image dimensions from sequence data to match translator
        // Find current ImageMap item (this component's parent) to get real dimensions
        const currentImageMapItem = sequence.items?.find(
          (item: SequenceItem) => item.id === props.id
        );

        // Use real image dimensions from the current item (1×1 solids → 1024 fallback)
        const createBasisSize = usableHotspotImageSize(
          currentImageMapItem?.pictures?.[0]?.width as number,
          currentImageMapItem?.pictures?.[0]?.height as number
        );
        const IMAGE_WIDTH = createBasisSize?.width ?? 1024;
        const IMAGE_HEIGHT = createBasisSize?.height ?? 1024;

        if (createBasisSize) {
          cachedImageDimensionsRef.current = createBasisSize;
        }

        const absoluteX = (data.coordinates.x * IMAGE_WIDTH) / 100;
        const absoluteY = (data.coordinates.y * IMAGE_HEIGHT) / 100;

        // Count existing audio points from children for position
        const audioChildrenCount = React.Children.toArray(children).filter(
          (child: React.ReactElement) =>
            child?.props?.type === AUDIO_POPUP || child?.props?.type === POPUP
        ).length;

        const subItemData = {
          sequenceId: sequence.id,
          data: {
            parent_id: props.id,
            position: audioChildrenCount + 1,
            type: 'Popup', // Backend uses 'Popup' for both AUDIO_POPUP and POPUP
            coordinates: `${Math.round(absoluteX)},${Math.round(absoluteY)}`,
            title: data.text,
            ...(data.subtitle && !data.audio
              ? { subtitle: escapeHtml(data.subtitle) }
              : {}),
            body: data.body || '',
            // Send just the audio ID as backend suggested
            audio_ids: data.audio ? [data.audio.id] : [],
            icon_type: data.iconType,
            ...(data.iconType === 'number' && data.iconValue != null
              ? { icon_value: data.iconValue }
              : {})
          },
          newItem: newPopupItem
        };

        const dataForSending = subItemData.data;
        if (!apiService) {
          console.error('❌ apiService not available');
          return false;
        }

        // The hotspot is added once the server has it; a refused one keeps
        // the panel's draft.
        const newItem = await apiService.addSubItem(
          subItemData.sequenceId,
          dataForSending
        );
        if (!newItem?.subitem?.id) return false;
        const realId = newItem.subitem.id;

        // Show loader at the clicked position until the position is saved
        setIsCreatingAudioPoint(true);
        setCreatingPosition(data.coordinates);

        const coordsPromise = apiService.updateSubItem(
          realId,
          'coordinates',
          dataForSending.coordinates
        );

        // Add to temporary state immediately for ALL points (with or without audio)
        const tempPoint = {
          id: realId,
          type: data.audio ? AUDIO_POPUP : POPUP,
          left: data.coordinates.x,
          top: data.coordinates.y,
          values: {
            title: data.text,
            body: data.body || '',
            mediaUrl: data.audio?.mediaUrl || '',
            name: data.audio?.originalFilename || 'Point',
            // Carried over so the marker is drawn as chosen right away
            // instead of falling back to '+' until the backend answers.
            iconType: data.iconType,
            iconValue: data.iconValue
          }
        };

        setTempAudioPoints(prev => [...prev, tempPoint]);

        // Update audio if provided (in parallel, non-blocking)
        if (data.audio) {
          const { updateFile } = useEditorStore.getState();
          updateFile(
            realId,
            { id: data.audio.id, mediaUrl: data.audio.mediaUrl },
            'audios'
          );
        }

        // Wait for coordinates to be saved, then ensure backend has the point
        coordsPromise
          .then(() => {
            setTimeout(() => {
              apiService.getSequenceInfo(sequence.id).then(freshSequence => {
                if (freshSequence?.sequence) {
                  setSequence(freshSequence.sequence);
                  // Clear temp point after confirming backend has it
                  setTimeout(() => {
                    setTempAudioPoints(prev =>
                      prev.filter(p => p.id !== realId)
                    );
                  }, 500); //
                }
              });
            }, 500); //

            // Hide loader after coordinates are saved
            setIsCreatingAudioPoint(false);
            setCreatingPosition(null);
          })
          .catch(error => {
            console.error('❌ Error updating coordinates:', error);
            // Remove temp point on error and hide loader
            setTempAudioPoints(prev => prev.filter(p => p.id !== realId));
            setIsCreatingAudioPoint(false);
            setCreatingPosition(null);
          });

        return true;
      } catch (error) {
        console.error('❌ Error in handleTextAudioSubmit:', error);
        return false;
      }
    };

    const handleAddTextAudioModalClose = () => {
      setShowAddTextAudioModal(false);
      setClickPosition(null);
      setEditingAudioPoint(null);
    };

    const handleEditAudioPoint = (audioPointData: AudioPointData) => {
      setEditingAudioPoint(audioPointData);
      setClickPosition({
        x: audioPointData.coordinates.x,
        y: audioPointData.coordinates.y
      });
      setShowAddTextAudioModal(true);
    };

    const handleDeleteAudioPoint = (audioPointId: string) => {
      if (!contentEditable || !sequence) return;
    };

    const handleMoveAudioPoint = (
      audioPointId: string,
      newCoordinates: { x: number; y: number }
    ) => {
      if (!contentEditable || !updateSequence || !sequence) return;

      // Parse the audio point ID using the utility function
      const idResult = parseAudioPointId(audioPointId);

      //i don't know why but the idResult.numericId is not working
      const _ids = audioPointId.split('-');
      idResult.numericId = parseInt(_ids[0], 10);

      // Only handle real backend IDs
      if (
        idResult.isTemporary ||
        !idResult.isValid ||
        idResult.numericId === null
      ) {
        console.error(
          'Invalid audio point ID for moving:',
          idResult.originalId
        );
        return;
      }

      const numericId = idResult.numericId;

      /** Full React item id, e.g. `{sequenceImageMapId}-0` — required so hotspots on the same card are distinct (prefix alone matches every popup). */
      const fixedOthers: { left: number; top: number }[] = [];
      React.Children.forEach(children as React.ReactNode, child => {
        if (!React.isValidElement(child)) return;
        if (child.props?.type !== AUDIO_POPUP && child.props?.type !== POPUP) {
          return;
        }
        const fullId = String(child.props.id);
        if (fullId === audioPointId) return;
        const mv =
          movedAudioPoints[fullId] ?? movedAudioPoints[fullId.split('-')[0]];
        fixedOthers.push({
          left: mv?.x ?? child.props.left,
          top: mv?.y ?? child.props.top
        });
      });
      tempAudioPoints.forEach(t => {
        fixedOthers.push({ left: t.left, top: t.top });
      });

      const nudged = nudgeDraggedHotspotAwayFromFixed(
        { left: newCoordinates.x, top: newCoordinates.y },
        fixedOthers,
        isMobile
      );
      const adjustedCoordinates = {
        x: nudged.left,
        y: nudged.top
      };

      // Get actual image dimensions from sequence data.
      const currentImageMapItem = sequence.items?.find(
        (item: SequenceItem) => item.id === props.id
      );
      const imageItem = sequence.items?.find(
        (item: SequenceItem) =>
          item.__typename === 'ImageMap' || item.type === 'ImageMap'
      );
      const backgroundItem = sequence.items?.find(
        (item: SequenceItem) =>
          item.__typename === 'Background' || item.type === 'Background'
      );

      // The Interactive4 translator reads only the ImageMap item's own picture.
      // If that picture exists but is a rejected 1×1 solid, do NOT rescue the
      // basis from other items/cache — use the same 1024 fallback as the translator.
      const ownPictureExists =
        currentImageMapItem?.pictures?.[0]?.width != null ||
        currentImageMapItem?.pictures?.[0]?.height != null;

      const sequenceSize = ownPictureExists
        ? usableHotspotImageSize(
            currentImageMapItem?.pictures?.[0]?.width as number,
            currentImageMapItem?.pictures?.[0]?.height as number
          )
        : (usableHotspotImageSize(
            imageItem?.pictures?.[0]?.width as number,
            imageItem?.pictures?.[0]?.height as number
          ) ??
          usableHotspotImageSize(
            backgroundItem?.pictures?.[0]?.width as number,
            backgroundItem?.pictures?.[0]?.height as number
          ));

      if (sequenceSize) {
        cachedImageDimensionsRef.current = sequenceSize;
      }

      const IMAGE_WIDTH =
        sequenceSize?.width ??
        (ownPictureExists
          ? 1024
          : (cachedImageDimensionsRef.current?.width ?? 1024));
      const IMAGE_HEIGHT =
        sequenceSize?.height ??
        (ownPictureExists
          ? 1024
          : (cachedImageDimensionsRef.current?.height ?? 1024));

      // Convert to absolute coordinates for backend using actual dimensions
      const absoluteX = (adjustedCoordinates.x * IMAGE_WIDTH) / 100;
      const absoluteY = (adjustedCoordinates.y * IMAGE_HEIGHT) / 100;
      const coordinatesString = `${Math.round(absoluteX)},${Math.round(
        absoluteY
      )}`;

      // Show moving loader
      setIsMovingAudioPoint(true);
      setMovingPosition(adjustedCoordinates);

      // Store the moved coordinates for immediate UI update
      setMovedAudioPoints(prev => ({
        ...prev,
        [audioPointId]: adjustedCoordinates
      }));

      // Use updateSequence to update backend
      updateSequence(numericId, 'coordinates', coordinatesString);

      // Hide loader after backend update (let optimistic rendering handle the UI)
      setTimeout(() => {
        setIsMovingAudioPoint(false);
        setMovingPosition(null);

        // DO NOT automatically clear moved coordinates - they will persist until manually refreshed
        // This ensures the audio point stays in the moved position regardless of backend delays
      }, 800);
    };

    // Extract audio ID from mediaUrl (e.g., "77531__en_1755702919.mp3" -> 77531)
    const extractAudioIdFromUrl = (url: string): number => {
      try {
        const filename = url.split('/').pop() || '';
        const match = filename.match(/^(\d+)__/);
        return match ? parseInt(match[1], 10) : 0;
      } catch {
        return 0;
      }
    };

    // Extract audio name from mediaUrl (remove ID prefix and timestamp)
    const extractAudioNameFromUrl = (url: string): string => {
      try {
        const filename = url.split('/').pop() || '';
        // Remove the ID prefix (77531__) and timestamp (?1755702919)
        const nameWithoutId = filename.replace(/^\d+__/, '').split('?')[0];
        return nameWithoutId || 'Selected Audio';
      } catch {
        return 'Selected Audio';
      }
    };

    const imageMapPopupLayoutValue = useMemo(
      () => ({
        popupFixedOffsetLeftPx,
        popupFixedOffsetTopPx,
        clampPopupToImageMap
      }),
      [popupFixedOffsetLeftPx, popupFixedOffsetTopPx, clampPopupToImageMap]
    );

    // Interactive4 (and similar): fill an aspect-ratio parent without distorting
    // the 400×420 hotspot %-space (absolute so % height resolves).
    const fillAspectParent = w === '100%' && h === '100%';

    return (
      <ImageMapPopupLayoutContext.Provider value={imageMapPopupLayoutValue}>
        <x.div
          w="100%"
          h="100%"
          overflow="hidden"
          position={fillAspectParent ? 'absolute' : undefined}
          top={fillAspectParent ? 0 : undefined}
          right={fillAspectParent ? 0 : undefined}
          bottom={fillAspectParent ? 0 : undefined}
          left={fillAspectParent ? 0 : undefined}
        >
          <x.div
            position="relative"
            ref={divElRef}
            data-image-map-anchor="true"
            w="100%"
            h="100%"
          >
            <Background
              className="bgImage"
              imageUrl={imageUrl}
              w={w}
              h={h}
              ref={backgroundRef}
              onClick={handleBackgroundClick}
              onMouseMove={handleBackgroundMouseMove}
              onMouseLeave={handleBackgroundMouseLeave}
              style={{
                cursor: contentEditable ? 'pointer' : 'default',
                ...(fillAspectParent
                  ? { width: '100%', height: '100%', maxWidth: 'none' }
                  : undefined)
              }}
              data-image-container="true"
            />
            {/* Render children: in editor skip AUDIO_POPUP and POPUP (we render AudioPoint below); in learner render all */}
            {React.Children.map(children, (child: React.ReactElement) => {
              if (contentEditable) {
                if (
                  child?.props?.type === AUDIO_POPUP ||
                  child?.props?.type === POPUP
                )
                  return null;
                return child;
              }
              return child;
            })}

            {/* Editor-only: render AudioPoint components for valid AUDIO_POPUP and POPUP items */}
            {contentEditable &&
              (() => {
                let audioPointNumber = 0;
                return React.Children.map(
                  children,
                  (child: React.ReactElement) => {
                    if (
                      child?.props?.type === AUDIO_POPUP ||
                      child?.props?.type === POPUP
                    ) {
                      const left = child.props.left;
                      const top = child.props.top;
                      const values = child.props.values || child.props;

                      // Skip if coordinates are invalid
                      if (
                        isNaN(left) ||
                        isNaN(top) ||
                        left == null ||
                        top == null
                      ) {
                        return null;
                      }

                      // Only count audio points — text-only (POPUP) show "+" not a number
                      if (child?.props?.type === AUDIO_POPUP) {
                        audioPointNumber++;
                      }
                      const hotspotInstanceId = String(child.props.id);

                      if (!hotspotInstanceId) {
                        return null; // Skip points without real backend IDs
                      }
                      const movedCoords =
                        movedAudioPoints[hotspotInstanceId] ??
                        movedAudioPoints[hotspotInstanceId.split('-')[0]];

                      // For POPUP: title (Title in modal), body (Body in modal)
                      // For AUDIO_POPUP: name has the backend title; values.title is the marker
                      const titleContent =
                        child?.props?.type === POPUP
                          ? (values.title ?? '')
                          : (values.name ?? '');

                      const bodyContent = values.body || '';
                      const hotspotId = hotspotInstanceId.split('-')[0];
                      // The hotspot's own subtitle, which learners see on a
                      // hotspot without audio (an audio popup carries none).
                      const savedSubtitle = sequence?.items
                        ?.flatMap((item: SequenceItem) => item.items || [])
                        .find(
                          (item: SequenceItem) => String(item.id) === hotspotId
                        )?.subtitle;

                      const audioPointData = {
                        id: hotspotId,
                        text: titleContent,
                        subtitle: stripHtml(savedSubtitle ?? undefined),
                        body: bodyContent,
                        iconType: values.iconType ?? null,
                        iconValue:
                          values.iconValue != null
                            ? Number(values.iconValue)
                            : null,
                        audio: values.mediaUrl
                          ? {
                              // Prefer the real backend audio id; only fall back
                              // to parsing the URL (legacy) when it's missing, so
                              // editing title/body doesn't drop the audio.
                              id:
                                Number(values.audioId) ||
                                extractAudioIdFromUrl(values.mediaUrl),
                              mediaUrl: values.mediaUrl,
                              originalFilename: extractAudioNameFromUrl(
                                values.mediaUrl
                              ),
                              name: extractAudioNameFromUrl(values.mediaUrl)
                            }
                          : null,
                        coordinates: movedCoords || {
                          x: left,
                          y: top
                        },
                        minX: 20,
                        maxX: 340,
                        minY: 20,
                        maxY: 360
                      };

                      const markerNumber =
                        values.iconType === 'number' && values.iconValue != null
                          ? Number(values.iconValue)
                          : typeof values.title === 'number'
                            ? values.title
                            : typeof values.btnTitle === 'number'
                              ? values.btnTitle
                              : audioPointNumber;

                      // Hide the AudioPoint if it's currently being edited or moved (show loader instead)
                      if (
                        (isEditingAudioPoint &&
                          editingPosition &&
                          Math.abs(
                            audioPointData.coordinates.x - editingPosition.x
                          ) < 0.1 &&
                          Math.abs(
                            audioPointData.coordinates.y - editingPosition.y
                          ) < 0.1) ||
                        (isMovingAudioPoint &&
                          movingPosition &&
                          Math.abs(
                            audioPointData.coordinates.x - movingPosition.x
                          ) < 0.1 &&
                          Math.abs(
                            audioPointData.coordinates.y - movingPosition.y
                          ) < 0.1)
                      ) {
                        return null; // Don't render this AudioPoint while editing or moving
                      }

                      return (
                        <AudioPoint
                          key={audioPointData.id}
                          data={audioPointData}
                          number={markerNumber}
                          onEdit={handleEditAudioPoint}
                          onMove={handleMoveAudioPoint}
                          onDelete={handleDeleteAudioPoint}
                        />
                      );
                    }
                    return null;
                  }
                );
              })()}

            {contentEditable &&
              tempAudioPoints.map(tempPoint => {
                const tempAudioPointData = {
                  id: tempPoint.id.toString(),
                  text:
                    tempPoint.type === POPUP
                      ? (tempPoint.values.title ?? '')
                      : (tempPoint.values.title ?? tempPoint.values.name ?? ''),
                  body: tempPoint.values.body || '',
                  iconType: tempPoint.values.iconType ?? null,
                  iconValue: tempPoint.values.iconValue ?? null,
                  audio: tempPoint.values.mediaUrl
                    ? {
                        id: 0, // Temporary
                        mediaUrl: tempPoint.values.mediaUrl,
                        originalFilename: tempPoint.values.name
                      }
                    : null,
                  coordinates: {
                    x: tempPoint.left,
                    y: tempPoint.top
                  }
                };

                return (
                  <AudioPoint
                    key={`temp-${tempPoint.id}`}
                    data={tempAudioPointData}
                    number={999} // High number so it doesn't conflict
                    onEdit={handleEditAudioPoint}
                    onMove={handleMoveAudioPoint}
                    onDelete={handleDeleteAudioPoint}
                  />
                );
              })}

            {/* Show loader while creating audio point */}
            {isCreatingAudioPoint && creatingPosition && (
              <div
                style={{
                  position: 'absolute',
                  left: `${creatingPosition.x}%`,
                  top: `${creatingPosition.y}%`,
                  transform: 'translate(-50%, -50%)',
                  zIndex: 10
                }}
              >
                <div
                  style={{
                    width: '32px',
                    height: '32px',
                    border: '3px solid #f3f3f3',
                    borderTop: '3px solid #9845fb',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}
                />
                <style>{`
                @keyframes spin {
                  0% { transform: rotate(0deg); }
                  100% { transform: rotate(360deg); }
                }
              `}</style>
              </div>
            )}

            {/* Show loader while editing audio point */}
            {isEditingAudioPoint && editingPosition && (
              <div
                style={{
                  position: 'absolute',
                  left: `${editingPosition.x}%`,
                  top: `${editingPosition.y}%`,
                  transform: 'translate(-50%, -50%)',
                  zIndex: 15,
                  width: '48px',
                  height: '48px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '10px'
                }}
              >
                <div
                  style={{
                    width: '32px',
                    height: '32px',
                    border: '3px solid #f3f3f3',
                    borderTop: '3px solid #9845fb',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}
                />
                <style>{`
                @keyframes spin {
                  0% { transform: rotate(0deg); }
                  100% { transform: rotate(360deg); }
                }
              `}</style>
              </div>
            )}

            {/* Show loader while moving audio point */}
            {isMovingAudioPoint && movingPosition && (
              <div
                style={{
                  position: 'absolute',
                  left: `${movingPosition.x}%`,
                  top: `${movingPosition.y}%`,
                  transform: 'translate(-50%, -50%)',
                  zIndex: 12,
                  width: '48px',
                  height: '48px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '10px'
                }}
              >
                <div
                  style={{
                    width: '32px',
                    height: '32px',
                    border: '3px solid #f3f3f3',
                    borderTop: '3px solid #9845fb',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}
                />
                <style>{`
                @keyframes spin {
                  0% { transform: rotate(0deg); }
                  100% { transform: rotate(360deg); }
                }
              `}</style>
              </div>
            )}

            {blockers.length > 0 && body && <Instructions>{body}</Instructions>}
            {contentEditable && (
              <EditBg
                divElRef={backgroundRef}
                id={props.id}
                type="image"
                updateFileHandler={updateBackgroundImage}
                imageGenerationPlacement="full_page_background"
              />
            )}
          </x.div>

          {/* Overlay to block interactions while creating, editing, or moving audio points */}
          {(isCreatingAudioPoint ||
            isEditingAudioPoint ||
            isMovingAudioPoint) && (
            <div
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.3)',
                zIndex: 50,
                cursor: 'wait',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              onClick={e => e.preventDefault()}
              onContextMenu={e => e.preventDefault()}
            >
              <div
                style={{
                  background: 'white',
                  padding: '20px 30px',
                  borderRadius: '12px',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '15px',
                  fontFamily: 'Raleway',
                  fontSize: '16px',
                  color: '#333'
                }}
              >
                <div
                  style={{
                    width: '24px',
                    height: '24px',
                    border: '3px solid #f3f3f3',
                    borderTop: '3px solid #9845fb',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}
                />
                {isCreatingAudioPoint
                  ? 'Creating audio point...'
                  : isEditingAudioPoint
                    ? 'Updating audio point...'
                    : 'Moving audio point...'}
              </div>
            </div>
          )}

          {/* Tooltip that follows mouse cursor in editor mode */}
          {mousePosition &&
            contentEditable &&
            !showAddTextAudioModal &&
            !isCreatingAudioPoint &&
            !isEditingAudioPoint &&
            !isMovingAudioPoint && (
              <div
                style={{
                  position: 'fixed',
                  left: mousePosition.x - 20,
                  top: mousePosition.y + 40,
                  width: '220px',
                  backgroundColor: '#fcf8e9',
                  color: 'black',
                  padding: '12px',
                  borderRadius: '10px',
                  border: '2px solid black',
                  fontSize: '14px',
                  fontFamily: 'Raleway, sans-serif',
                  pointerEvents: 'none',
                  zIndex: 1000,
                  whiteSpace: 'normal'
                }}
              >
                <svg
                  width="16"
                  height="11"
                  viewBox="0 0 16 11"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  style={{
                    position: 'absolute',
                    top: '-10px',
                    left: '20px'
                  }}
                >
                  <path d="M0 8L16 8L8 0L0 8Z" fill="black" />
                  <path d="M0 10.5L16 10.5L8 2.5L0 10.5Z" fill="#FCF8E9" />
                </svg>
                Select where you'd like to add text or audio on the image
              </div>
            )}

          {/* Add Text and Audio Modal */}
          <AddTextAudioModal
            isOpen={showAddTextAudioModal}
            onClose={handleAddTextAudioModalClose}
            onSubmit={handleTextAudioSubmit}
            offerSubtitle={offerPopupSubtitle}
            clickPosition={clickPosition}
            editingData={editingAudioPoint as AudioPointData | undefined}
            title={'Add text or audio point'}
          />
        </x.div>
      </ImageMapPopupLayoutContext.Provider>
    );
  }
}

registry[IMAGE_MAP] = ImageMap;
